import React, {
	useState,
	useMemo,
	createContext,
	useContext,
	type PropsWithChildren,
	useCallback,
} from 'react';
import type { InvitePeopleModalProps } from '@jira/project-settings__invite-people-modal-project/src/ModalBodyProps.types.tsx';
import InvitePeopleModalAsync from '@jira/project-settings__invite-people-modal-project/src/AsyncCoreInvitesProjectDialogComponent.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';

const noop = () => null;

type JiraInvitePeopleModalContextProviderProps = Omit<
	InvitePeopleModalProps,
	'appEdition' | 'cloudId' | 'isOpen' | 'isSiteAdmin' | 'onClose' | 'onSubmit'
	// external use of "onSubmit" is forbidden or this bypasses the invite to project logic
> & { onSubmit?: never };

type JiraInvitePeopleModalContextProviderState = {
	props: JiraInvitePeopleModalContextProviderProps;
	isOpen: boolean;
};

type OnOpen = (state?: JiraInvitePeopleModalContextProviderProps) => void;

type JiraInvitePeopleModalContextProps = {
	isOpen: boolean;
	onOpen: OnOpen;
	onClose: () => void;
};

const JiraInvitePeopleModalContext = createContext<JiraInvitePeopleModalContextProps>({
	isOpen: false,
	onOpen: noop,
	onClose: noop,
});

// modal props can be passed to the provider or set in state when onOpen is called
export const JiraInvitePeopleModalContextProvider = ({
	children,
	...props
}: PropsWithChildren<JiraInvitePeopleModalContextProviderProps>): React.JSX.Element => {
	// new invite people modal requirements
	const cloudId = useCloudId();
	const appEditions = useAppEditions();
	const isSiteAdmin = useIsSiteAdmin();

	const [providerState, setProviderState] = useState<JiraInvitePeopleModalContextProviderState>({
		props: {},
		isOpen: false,
	});

	const onClose = useCallback(() => {
		setProviderState({ props: {}, isOpen: false });
	}, []);

	const onOpen = useCallback<OnOpen>((state) => {
		setProviderState({ props: state ?? {}, isOpen: true });
	}, []);

	const value: JiraInvitePeopleModalContextProps = useMemo(
		() => ({
			isOpen: providerState.isOpen,
			onOpen,
			onClose,
		}),
		[providerState.isOpen, onOpen, onClose],
	);

	return (
		<JiraInvitePeopleModalContext.Provider value={value}>
			{providerState.isOpen ? (
				<InvitePeopleModalAsync
					appEdition={appEditions.software}
					cloudId={cloudId}
					isOpen={providerState.isOpen}
					onClose={onClose}
					isSiteAdmin={isSiteAdmin}
					isAdmin={isSiteAdmin}
					{...props}
					// props set in state override props passed to provider
					{...providerState.props}
				/>
			) : null}
			{children}
		</JiraInvitePeopleModalContext.Provider>
	);
};

export const useJiraInvitePeopleModal = (): JiraInvitePeopleModalContextProps =>
	useContext(JiraInvitePeopleModalContext);

// used to mimic useInvitePeopleModal.tsx
// platform/packages/people-and-teams/invite-people/src/components/InvitePeopleModal/useInvitePeopleModal.tsx
export const useJiraInvitePeopleModalBound = (
	props: JiraInvitePeopleModalContextProviderProps,
): {
	isOpen: boolean;
	openModal: () => void;
	closeModal: () => void;
} => {
	const { isOpen, onOpen, onClose } = useContext(JiraInvitePeopleModalContext);
	const openModal = useCallback((): void => onOpen(props), [onOpen, props]);
	return { isOpen, openModal, closeModal: onClose };
};
